import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { prefersReducedMotion, TRACK_COMPLETED_EVENT } from "../lib/celebration";

/** The theme's own chart colours, so the party reads in light and dark. */
const COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => `var(--color-chart-${n})`);
const PIECES = 90;
/** Long enough for the slowest piece to fall past the bottom. */
const LASTS_MS = 4200;

interface Piece {
  left: number;
  delay: number;
  duration: number;
  drift: number;
  spin: number;
  size: number;
  round: boolean;
  color: string;
}

function pieces(): Piece[] {
  return Array.from({ length: PIECES }, (_, index) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 2.4 + Math.random() * 1.4,
    drift: (Math.random() - 0.5) * 30,
    spin: 360 + Math.random() * 720,
    size: 6 + Math.random() * 6,
    round: Math.random() < 0.3,
    color: COLORS[index % COLORS.length]!,
  }));
}

/**
 * Confetti over everything — the track player is a dialog, so it portals to
 * the body above it — for a few seconds after a track is finished. Never in
 * the way: no pointer events, hidden from assistive tech (the toast is what is
 * announced), and nothing at all with reduced motion.
 */
export function Confetti() {
  const [burst, setBurst] = useState<{ id: number; pieces: Piece[] } | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    const onCompleted = () => {
      if (prefersReducedMotion()) return;
      window.clearTimeout(timer);
      setBurst({ id: Date.now(), pieces: pieces() });
      timer = window.setTimeout(() => setBurst(null), LASTS_MS);
    };
    window.addEventListener(TRACK_COMPLETED_EVENT, onCompleted);
    return () => {
      window.removeEventListener(TRACK_COMPLETED_EVENT, onCompleted);
      window.clearTimeout(timer);
    };
  }, []);

  if (!burst) return null;
  return createPortal(
    <div key={burst.id} aria-hidden data-testid="confetti" className="pointer-events-none fixed inset-0 z-[100] overflow-hidden">
      {burst.pieces.map((piece, index) => (
        <span
          key={index}
          className={`confetti-piece absolute top-0 block ${piece.round ? "rounded-full" : "rounded-[1px]"}`}
          style={{
            left: `${piece.left}%`,
            width: piece.size,
            height: piece.round ? piece.size : piece.size * 0.45,
            backgroundColor: piece.color,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            "--drift": `${piece.drift}vw`,
            "--spin": `${piece.spin}deg`,
          } as CSSProperties}
        />
      ))}
    </div>,
    document.body,
  );
}
